import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ImageUploadProps {
  onImageChange: (file: File | null) => void;
  value?: string;
  maxSizeKB?: number;
  allowedTypes?: string[];
  helperText?: string;
  error?: string;
  className?: string;
  id?: string;
}

export const ImageUpload = ({
  onImageChange,
  maxSizeKB = 500,
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  helperText,
  error,
  className = '',
  id,
  value,
}: ImageUploadProps) => {
  const [preview, setPreview] = useState<string | null>(value || null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setPreview(value);
    }
  }, [value]);

  const handleFile = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }

      onImageChange(file);

      // Создание preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onImageChange, allowedTypes, maxSizeKB],
  );

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return `Разрешены только: ${allowedTypes.map((t) => t.split('/')[1]).join(', ')}`;
    }

    const maxSizeBytes = maxSizeKB * 1024;
    if (file.size > maxSizeBytes) {
      return `Максимальный размер: ${maxSizeKB}кб`;
    }

    return null;
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  const handleRemove = useCallback(() => {
    setPreview(null);
    onImageChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onImageChange]);

  return (
    <div className={className}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept={allowedTypes.join(',')}
        className="hidden"
        id={id}
      />

      {preview ? (
        <div className="relative">
          <img src={preview} alt="Preview" className="h-48 object-cover rounded-lg border w-48" />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
          >
            Удалить
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50' : error ? 'border-red-300' : 'border-gray-300'}
            hover:border-gray-400 transition-colors
          `}
        >
          <p className="text-gray-700 mb-1">
            {isDragging ? 'Отпустите для загрузки' : 'Нажмите или перетащите изображение'}
          </p>
          <p className="text-sm text-gray-500">PNG, JPG, WebP до {maxSizeKB}кб</p>
        </div>
      )}

      {helperText && !error && <p className="mt-2 text-sm text-gray-500">{helperText}</p>}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};
